
import { DocumentReference, getDoc, Firestore, doc } from "firebase/firestore";
import { db as defaultDb } from "@/services/firebaseService";

/**
 * Fetch user data including PII from Firestore by user ID.
 *
 * Returns `null` only when the user document genuinely does not exist. Any
 * other Firestore failure — notably `permission-denied` — THROWS, so callers
 * can tell "no such user" apart from "you may not see this user".
 *
 * KB-2306: this used to wrap everything in one try/catch that returned `null`,
 * which collapsed both outcomes into the same value and rendered a denied read
 * as a plausible-looking `User a1b2c3d4` placeholder. Under the narrowed
 * `users/{userId}` read rule (KB-2291) the main-document read is denied
 * whenever the editor being attributed is a KB admin (no `organizationId`) or
 * belongs to another org, so that silent degradation would have become the
 * common case rather than an edge case.
 *
 * The PII sub-read keeps its own catch on purpose: `users/{uid}/private/pii` is
 * owner-or-admin only, so a non-admin attributing a *peer* is denied the name
 * even when the main doc is readable. That is expected and must degrade to the
 * email/uid fallback instead of failing the whole lookup — previously it threw
 * into the outer catch and nulled the entire result, which is why attribution
 * showed raw uids for non-admin viewers even before KB-2291.
 */
export const fetchUserData = async (db: Firestore, userId: string) => {
  const userDoc = await getDoc(doc(db, "users", userId));

  if (!userDoc.exists()) {
    console.log(`[fetchUserData] User document not found for ID: ${userId}`);
    return null;
  }

  const userData = userDoc.data();

  let piiData: { firstName?: string; lastName?: string } = {};
  try {
    const piiDoc = await getDoc(doc(db, "users", userId, "private", "pii"));
    if (piiDoc.exists()) {
      piiData = piiDoc.data() as { firstName?: string; lastName?: string };
    }
  } catch (piiError) {
    console.log(`[fetchUserData] PII not readable for ${userId}; falling back to email/uid`, piiError);
  }

  return {
    firstName: piiData.firstName || '',
    lastName: piiData.lastName || '',
    email: userData.email || '',
    // Include any other fields that might be needed
    ...userData,
    ...piiData
  };
};

/**
 * Format a user into a display name with first name and last initial
 * @param user The user object or reference
 * @returns Formatted display name or null if no user data
 */
export function getUserDisplayNameById(user: any): string | null {
  if (!user) return null;
  
  // If user is a string (just an ID), return it for now
  // The component should use this ID to fetch the full user data
  if (typeof user === 'string') {
    return user;
  }
  
  // If user has firstName and lastName properties, format them
  if (user.firstName || user.lastName) {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    const lastInitial = lastName ? lastName.charAt(0) + '.' : '';
    
    if (firstName && lastInitial) {
      return `${firstName} ${lastInitial}`;
    }
    return firstName || null;
  }
  
  // If user has a displayName property, format it
  if (user.displayName) {
    const nameParts = user.displayName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts[1] || '';
    const lastInitial = lastName ? lastName.charAt(0) + '.' : '';
    
    if (firstName && lastInitial) {
      return `${firstName} ${lastInitial}`;
    }
    return firstName || null;
  }
  
  // If user has an email property, use that
  if (user.email) {
    return user.email;
  }
  
  // If user has an id property, use that
  if (user.id) {
    return user.id;
  }
  
  // Last resort: try to convert to string
  try {
    return String(user);
  } catch (e) {
    console.error('Failed to format user display name:', user);
    return null;
  }
}

/**
 * Helper function to extract ID from any reference or string
 */
export const extractId = (ref: any): string => {
  if (!ref) return '';
  
  // If ref is a string, return it directly
  if (typeof ref === 'string') {
    return ref;
  }
  
  // If ref has an id property that's a string, return that
  if (ref && typeof ref.id === 'string') {
    return ref.id;
  }
  
  // If ref is an object with a path property, extract the ID from the path
  if (ref && typeof ref.path === 'string') {
    const pathParts = ref.path.split('/');
    return pathParts[pathParts.length - 1];
  }
  
  // Last resort: try to convert to string and return
  try {
    return String(ref);
  } catch (e) {
    console.error('Failed to extract ID from reference:', ref);
    return '';
  }
};
